import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loansApi, loanProductsApi, clientsApi, api } from '../lib/api';
import { formatDate, formatCurrency, getStatusColor, capitalizeFirst, formatPercentage } from '../lib/utils';
import {
  Wallet, Plus, Search, Filter, MoreHorizontal, Eye, Check, X, DollarSign,
  ChevronLeft, ChevronRight, FileText, Calendar, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';

const LoansPage = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const officeId = searchParams.get('office_id');
  const officeName = searchParams.get('office_name');

  useEffect(() => {
    const status = searchParams.get('status');
    if (status) setStatusFilter(status);
    if (searchParams.get('action') === 'new') setShowNewModal(true);
  }, [searchParams]);

  useEffect(() => {
    loadLoans();
  }, [page, statusFilter, officeId]);

  const loadLoans = async () => {
    setLoading(true);
    try {
      const params = { skip: page * 20, limit: 20 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (officeId) params.office_id = officeId;
      const response = await loansApi.list(params);
      setLoans(response.data.loans);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewLoan = async (loanId) => {
    try {
      const response = await loansApi.get(loanId);
      setSelectedLoan(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load loan:', error);
    }
  };

  const approveLoan = async (loanId) => {
    try {
      await loansApi.approve(loanId);
      loadLoans();
      if (selectedLoan?.id === loanId) {
        viewLoan(loanId);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve loan');
    }
  };

  const rejectLoan = async (loanId, reason) => {
    try {
      await loansApi.reject(loanId, reason);
      loadLoans();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject loan');
    }
  };

  const disburseLoan = async (loanId) => {
    try {
      await loansApi.disburse(loanId);
      loadLoans();
      if (selectedLoan?.id === loanId) {
        viewLoan(loanId);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to disburse loan');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="loans-page">
      {/* Office Filter Banner */}
      {officeId && (
        <div className="bg-[#1f1f1f]/5 border border-[#1f1f1f]/10 px-4 py-2 flex items-center justify-between" data-testid="office-filter-banner">
          <span className="text-sm text-[#1f1f1f]">Filtered by office: <strong>{decodeURIComponent(officeName || 'Selected Office')}</strong></span>
          <div className="flex gap-2">
            <button onClick={() => navigate(`/dashboard/clients?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Clients</button>
            <button onClick={() => navigate(`/dashboard/savings?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Savings</button>
            <button onClick={() => navigate('/dashboard/loans')} className="text-xs text-red-500 hover:underline">Clear Filter</button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            LOAN MANAGEMENT
          </p>
          <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
            Loans
          </h1>
        </div>
        <Button
          onClick={() => setShowNewModal(true)}
          className="bg-[#1f1f1f] hover:bg-[#333333]"
          data-testid="new-loan-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Loan Application
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          onClick={() => setStatusFilter('all')}
          className={`p-4 border text-left transition-colors ${statusFilter === 'all' ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-2xl font-bold text-[#1f1f1f]">{total}</p>
          <p className="text-xs uppercase tracking-wider text-gray-500">All Loans</p>
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`p-4 border text-left transition-colors ${statusFilter === 'pending' ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-2xl font-bold text-amber-600">{loans.filter(l => l.status === 'pending').length}</p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Pending</p>
        </button>
        <button
          onClick={() => setStatusFilter('disbursed')}
          className={`p-4 border text-left transition-colors ${statusFilter === 'disbursed' ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-2xl font-bold text-emerald-600">{loans.filter(l => l.status === 'disbursed' || l.status === 'active').length}</p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Active</p>
        </button>
        <button
          onClick={() => setStatusFilter('defaulted')}
          className={`p-4 border text-left transition-colors ${statusFilter === 'defaulted' ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-2xl font-bold text-red-600">{loans.filter(l => l.status === 'defaulted').length}</p>
          <p className="text-xs uppercase tracking-wider text-gray-500">Defaulted</p>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="loans-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Loan
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Client
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Outstanding
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Term
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : loans.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No loans found
                  </td>
                </tr>
              ) : (
                loans.map((loan) => (
                  <tr key={loan.id} className="hover:bg-gray-50" data-testid={`loan-row-${loan.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{loan.loan_number}</p>
                      <p className="text-sm text-gray-500">{formatDate(loan.created_at)}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {loan.client_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(loan.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-right">
                      {formatCurrency(loan.outstanding_balance)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-center">
                      {loan.term_months} months
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${getStatusColor(loan.status)}`}>
                        {capitalizeFirst(loan.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`loan-actions-${loan.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewLoan(loan.id)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          {loan.status === 'pending' && (
                            <>
                              <DropdownMenuItem onClick={() => approveLoan(loan.id)}>
                                <Check className="w-4 h-4 mr-2" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => rejectLoan(loan.id, 'Rejected by officer')}>
                                <X className="w-4 h-4 mr-2" /> Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {loan.status === 'approved' && (
                            <DropdownMenuItem onClick={() => disburseLoan(loan.id)}>
                              <DollarSign className="w-4 h-4 mr-2" /> Disburse
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * 20 + 1} to {Math.min((page + 1) * 20, total)} of {total} loans
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* New Loan Modal */}
      <NewLoanModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={() => {
          setShowNewModal(false);
          loadLoans();
        }}
      />

      {/* Loan Detail Modal */}
      <LoanDetailModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        loan={selectedLoan}
        onApprove={approveLoan}
        onDisburse={disburseLoan}
        onRefresh={() => viewLoan(selectedLoan?.id)}
      />
    </div>
  );
};

const NewLoanModal = ({ open, onClose, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '', product_id: '', amount: '', term_months: '', purpose: '',
    collateral_description: '', collateral_value: '', guarantor_name: '',
    guarantor_phone: '', guarantor_id_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadProducts();
      loadClients();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      const response = await loanProductsApi.list();
      setProducts(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsApi.list({ status: 'active', limit: 100 });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      amount: product?.min_amount || '',
      term_months: product?.min_term_months || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        term_months: parseInt(formData.term_months),
        collateral_value: formData.collateral_value ? parseFloat(formData.collateral_value) : null
      };
      await loansApi.create(data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create loan application');
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimates = () => {
    if (!selectedProduct || !formData.amount || !formData.term_months) return null;
    const amount = parseFloat(formData.amount);
    const term = parseInt(formData.term_months);
    const rate = selectedProduct.interest_rate;
    const totalInterest = amount * (rate / 100) * (term / 12);
    const totalAmount = amount + totalInterest;
    const monthlyPayment = totalAmount / term;
    return { totalInterest, totalAmount, monthlyPayment };
  };

  const estimates = calculateEstimates();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Loan Application</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Client *</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger data-testid="loan-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} ({c.client_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Loan Product *</Label>
              <Select value={formData.product_id} onValueChange={handleProductChange}>
                <SelectTrigger data-testid="loan-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({formatPercentage(p.interest_rate)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedProduct && (
            <div className="p-4 bg-gray-50 border border-gray-200 text-sm">
              <p className="font-medium text-gray-900 mb-2">{selectedProduct.name}</p>
              <div className="grid grid-cols-3 gap-4 text-gray-600">
                <div>
                  <span className="text-gray-500">Amount Range:</span>
                  <br />{formatCurrency(selectedProduct.min_amount)} - {formatCurrency(selectedProduct.max_amount)}
                </div>
                <div>
                  <span className="text-gray-500">Term Range:</span>
                  <br />{selectedProduct.min_term_months} - {selectedProduct.max_term_months} months
                </div>
                <div>
                  <span className="text-gray-500">Interest Rate:</span>
                  <br />{formatPercentage(selectedProduct.interest_rate)} p.a.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Loan Amount (ZAR) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                required
                data-testid="loan-amount-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Term (Months) *</Label>
              <Input
                type="number"
                value={formData.term_months}
                onChange={(e) => setFormData(prev => ({ ...prev, term_months: e.target.value }))}
                required
                data-testid="loan-term-input"
              />
            </div>
          </div>

          {estimates && (
            <div className="p-4 bg-[#1f1f1f] text-white">
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">Loan Estimate</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(estimates.monthlyPayment)}</p>
                  <p className="text-xs text-gray-400">Monthly Payment</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(estimates.totalInterest)}</p>
                  <p className="text-xs text-gray-400">Total Interest</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#C5A059]">{formatCurrency(estimates.totalAmount)}</p>
                  <p className="text-xs text-gray-400">Total Repayment</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Loan Purpose</Label>
            <Textarea
              value={formData.purpose}
              onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
              placeholder="Describe the purpose of this loan..."
              rows={3}
            />
          </div>

          {selectedProduct?.requires_collateral && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Collateral Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
                  <Input
                    value={formData.collateral_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, collateral_description: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Value (ZAR)</Label>
                  <Input
                    type="number"
                    value={formData.collateral_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, collateral_value: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-loan-application">
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LoanDetailModal = ({ open, onClose, loan, onApprove, onDisburse, onRefresh }) => {
  const [showRepayment, setShowRepayment] = useState(false);
  const [repaymentAmount, setRepaymentAmount] = useState('');
  const [repaymentLoading, setRepaymentLoading] = useState(false);

  if (!loan) return null;

  const handleRepayment = async () => {
    setRepaymentLoading(true);
    try {
      await loansApi.repayment(loan.id, { amount: parseFloat(repaymentAmount) });
      setShowRepayment(false);
      setRepaymentAmount('');
      onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to record repayment');
    } finally {
      setRepaymentLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-3">
            <Wallet className="w-6 h-6 text-[#C5A059]" />
            <div>
              <p>{loan.loan_number}</p>
              <p className="text-sm font-normal text-gray-500">{loan.client?.name}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status & Actions */}
          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium ${getStatusColor(loan.status)}`}>
                {capitalizeFirst(loan.status)}
              </span>
              <span className="text-sm text-gray-500">
                Created: {formatDate(loan.created_at)}
              </span>
            </div>
            <div className="flex gap-2">
              {loan.status === 'pending' && (
                <>
                  <Button size="sm" onClick={() => onApprove(loan.id)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </>
              )}
              {loan.status === 'approved' && (
                <Button size="sm" onClick={() => onDisburse(loan.id)} className="bg-[#C5A059] hover:bg-[#D4B16A]">
                  <DollarSign className="w-4 h-4 mr-1" /> Disburse
                </Button>
              )}
              {(loan.status === 'disbursed' || loan.status === 'active') && (
                <Button size="sm" onClick={() => setShowRepayment(true)} className="bg-[#1f1f1f] hover:bg-[#333333]">
                  <DollarSign className="w-4 h-4 mr-1" /> Record Payment
                </Button>
              )}
            </div>
          </div>

          {/* Loan Summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 border border-gray-200">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Principal</p>
              <p className="text-xl font-bold text-[#1f1f1f]">{formatCurrency(loan.amount)}</p>
            </div>
            <div className="p-4 border border-gray-200">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Interest</p>
              <p className="text-xl font-bold text-[#1f1f1f]">{formatCurrency(loan.total_interest)}</p>
            </div>
            <div className="p-4 border border-gray-200">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Total</p>
              <p className="text-xl font-bold text-[#1f1f1f]">{formatCurrency(loan.total_amount)}</p>
            </div>
            <div className="p-4 border border-gray-200 bg-[#1f1f1f]">
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Outstanding</p>
              <p className="text-xl font-bold text-[#C5A059]">{formatCurrency(loan.outstanding_balance)}</p>
            </div>
          </div>

          {/* Loan Details */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Loan Terms</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Interest Rate</span>
                  <span className="font-medium">{formatPercentage(loan.interest_rate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Term</span>
                  <span className="font-medium">{loan.term_months} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monthly Payment</span>
                  <span className="font-medium">{formatCurrency(loan.monthly_payment)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Purpose</span>
                  <span className="font-medium">{loan.purpose || '-'}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Credit Assessment</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Credit Score at Application</span>
                  <span className={`font-medium ${
                    loan.credit_score_at_application >= 700 ? 'text-emerald-600' :
                    loan.credit_score_at_application >= 600 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {loan.credit_score_at_application || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Credit Life Insurance */}
          <LoanInsurancePanel loanId={loan.id} loanStatus={loan.status} onRefresh={onRefresh} />

          {/* Covenant Status (for applicable loan categories) */}
          {loan.covenant_status && (
            <div className="p-4 border border-gray-200 rounded" data-testid="loan-covenant-status">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Covenant Status</h4>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${loan.covenant_status.breached ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                  {loan.covenant_status.breached ? 'BREACH' : 'COMPLIANT'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {loan.covenant_status.covenants?.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.compliant ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <span className="text-gray-500">{c.label}</span>
                    <span className={`font-mono font-semibold ml-auto ${c.compliant ? 'text-gray-700' : 'text-red-600'}`}>{c.current_value?.toFixed(2)}</span>
                    <span className="text-gray-400 text-[10px]">{c.operator}{c.threshold}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Repayment Schedule */}
          {loan.repayment_schedule?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Repayment Schedule</h4>
              <div className="border border-gray-200 max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">#</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Due Date</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Principal</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Interest</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Total</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loan.repayment_schedule.map((installment) => (
                      <tr key={installment.id}>
                        <td className="px-4 py-2">{installment.installment_number}</td>
                        <td className="px-4 py-2">{formatDate(installment.due_date)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(installment.principal_due)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(installment.interest_due)}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(installment.total_due)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 text-xs ${getStatusColor(installment.status)}`}>
                            {capitalizeFirst(installment.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Repayment Dialog */}
        <Dialog open={showRepayment} onOpenChange={setShowRepayment}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Repayment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount (ZAR)</Label>
                <Input
                  type="number"
                  value={repaymentAmount}
                  onChange={(e) => setRepaymentAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="repayment-amount-input"
                />
              </div>
              <p className="text-sm text-gray-500">
                Outstanding Balance: {formatCurrency(loan.outstanding_balance)}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRepayment(false)}>Cancel</Button>
              <Button onClick={handleRepayment} disabled={repaymentLoading} className="bg-[#1f1f1f]" data-testid="confirm-repayment">
                {repaymentLoading ? 'Processing...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

/* ========== Credit Life Insurance panel inside Loan Detail modal ========== */
const LoanInsurancePanel = ({ loanId, loanStatus, onRefresh }) => {
  const [policy, setPolicy] = useState(null);
  const [eligible, setEligible] = useState(null);  // {schemes:[], primary_scheme_id, allow_byo}
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [mode, setMode] = useState('inhouse');  // 'inhouse' | 'byo'
  const [schemeId, setSchemeId] = useState('');
  const [byo, setByo] = useState({ external_insurer_name: '', external_policy_number: '', external_fsp_number: '', cover_amount: '', policy_start_date: '', policy_end_date: '' });

  useEffect(() => {
    if (!loanId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [pRes, eRes] = await Promise.all([
          api.get(`/loans/${loanId}/insurance/policy`),
          api.get(`/loans/${loanId}/insurance/eligible-schemes`).catch(() => ({ data: { schemes: [], allow_byo: true } })),
        ]);
        if (!active) return;
        setPolicy(pRes.data?.policy || null);
        setEligible(eRes.data || null);
        const primary = eRes.data?.primary_scheme_id || (eRes.data?.schemes?.[0]?.id ?? '');
        if (primary) setSchemeId(primary);
      } catch (e) {
        if (active) { setPolicy(null); setEligible(null); }
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [loanId]);

  const issuePolicy = async () => {
    setIssuing(true);
    try {
      let payload;
      if (mode === 'byo') {
        if (!byo.external_insurer_name || !byo.external_policy_number || !byo.external_fsp_number || !byo.cover_amount) {
          alert('Insurer name, policy number, FSP number and cover amount are required for BYO insurance.');
          setIssuing(false);
          return;
        }
        payload = { is_byo: true, ...byo, cover_amount: parseFloat(byo.cover_amount) };
      } else {
        if (!schemeId) { alert('Select a scheme'); setIssuing(false); return; }
        payload = { scheme_id: schemeId };
      }
      const { data } = await api.post(`/loans/${loanId}/insurance/policy`, payload);
      alert(data.message === 'policy_already_exists'
        ? 'A policy already exists for this loan.'
        : `Policy ${data.policy_number} issued${data.is_byo ? ' (BYO)' : ''}. Cover: R${Number(data.cover_amount).toLocaleString()}`);
      setShowIssue(false);
      const { data: p } = await api.get(`/loans/${loanId}/insurance/policy`);
      setPolicy(p?.policy || null);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to issue policy');
    }
    setIssuing(false);
  };

  if (loading) return null;
  const disburseable = loanStatus === 'disbursed' || loanStatus === 'active';
  const canIssue = disburseable && !policy && (eligible?.schemes?.length > 0 || eligible?.allow_byo);

  return (
    <div className="p-4 border border-gray-200" data-testid="loan-cli-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Credit Life Insurance (CLI)</h4>
        {policy ? (
          <span className={`px-2 py-0.5 text-[10px] font-bold ${policy.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {policy.is_byo ? 'BYO • ' : ''}{policy.status?.toUpperCase()}
          </span>
        ) : canIssue ? (
          <Button size="sm" className="bg-[#C5A059] hover:bg-[#b38f4e] text-white text-xs h-7" onClick={() => setShowIssue(true)} data-testid="issue-policy-btn">
            Issue CLI Policy
          </Button>
        ) : null}
      </div>

      {policy ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><p className="text-gray-500">Policy No.</p><p className="font-mono font-semibold">{policy.policy_number}</p></div>
            <div><p className="text-gray-500">Insurer</p><p className="font-medium">{policy.insurer_name} <span className="text-gray-400">({policy.insurer_fsp_number})</span></p></div>
            <div><p className="text-gray-500">Cover</p><p className="font-mono font-semibold">R {Number(policy.cover_amount || 0).toLocaleString()}</p></div>
            {policy.is_byo ? (
              <div><p className="text-gray-500">External Policy #</p><p className="font-mono">{policy.external_policy_number || '—'}</p></div>
            ) : (
              <>
                <div><p className="text-gray-500">Premium Posted</p><p className="font-mono">R {Number(policy.total_premium_posted || 0).toLocaleString()}</p></div>
                <div><p className="text-gray-500">Model</p><p className="capitalize">{policy.premium_model?.replace('_', ' ')}</p></div>
                <div><p className="text-gray-500">Rate /R1000</p><p className="font-mono">R{Number(policy.premium_rate_per_1000 || 0).toFixed(2)}</p></div>
                <div><p className="text-gray-500">Last Posted</p><p>{policy.last_premium_posted_at?.slice(0, 10) || '—'}</p></div>
                <div><p className="text-gray-500">Commission Earned</p><p className="font-mono">R {Number(policy.total_commission_earned || 0).toLocaleString()}</p></div>
              </>
            )}
          </div>

          {/* Policy documents (Cession of Rights for BYO, etc.) */}
          {(policy.documents?.length > 0 || policy.is_byo) && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                  Policy Documents ({policy.documents?.length || 0})
                </p>
                {policy.is_byo && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={async () => {
                    try {
                      await api.post(`/insurance/policies/${policy.id}/regenerate-cession`);
                      const { data: p } = await api.get(`/loans/${loanId}/insurance/policy`);
                      setPolicy(p?.policy || null);
                    } catch (e) { alert('Regenerate failed'); }
                  }} data-testid="regenerate-cession-btn">
                    Regenerate Cession PDF
                  </Button>
                )}
              </div>
              {policy.documents?.length > 0 ? (
                <div className="space-y-1">
                  {policy.documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between text-xs p-2 border border-gray-100 hover:bg-gray-50" data-testid={`policy-doc-${d.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                        <span className="truncate">{d.name}</span>
                        {d.auto_generated && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700">AUTO</span>}
                        <span className="text-gray-400 capitalize">{d.type?.replace(/_/g, ' ')}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        try {
                          const resp = await api.get(`/insurance/policies/${policy.id}/documents/${d.id}/download`, { responseType: 'blob' });
                          const url = URL.createObjectURL(resp.data);
                          window.open(url, '_blank');
                        } catch (e) { alert('Download failed'); }
                      }} className="text-[#C5A059]" data-testid={`download-policy-doc-${d.id}`}>Download</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No documents yet. Click <b>Regenerate Cession PDF</b> to create one.</p>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-gray-400 italic">
          {!disburseable
            ? 'CLI can be issued once the loan is disbursed.'
            : (eligible?.schemes?.length === 0 && !eligible?.allow_byo)
              ? 'No eligible CLI scheme is configured for this product.'
              : 'No policy issued yet.'}
        </p>
      )}

      {/* Issue modal */}
      {showIssue && (
        <Dialog open={true} onOpenChange={() => setShowIssue(false)}>
          <DialogContent aria-describedby="issue-desc" className="max-w-lg">
            <DialogHeader><DialogTitle>Issue Credit Life Insurance Policy</DialogTitle></DialogHeader>
            <p id="issue-desc" className="sr-only">Choose an in-house scheme or capture the borrower's own external insurance.</p>
            <div className="flex gap-2 border border-gray-200 w-fit">
              {eligible?.schemes?.length > 0 && (
                <button type="button" onClick={() => setMode('inhouse')} className={`px-3 py-1.5 text-xs ${mode === 'inhouse' ? 'bg-[#1f1f1f] text-white' : 'text-gray-600'}`} data-testid="mode-inhouse">In-house Scheme</button>
              )}
              {eligible?.allow_byo && (
                <button type="button" onClick={() => setMode('byo')} className={`px-3 py-1.5 text-xs ${mode === 'byo' ? 'bg-[#1f1f1f] text-white' : 'text-gray-600'}`} data-testid="mode-byo">Bring Your Own (Client's Insurer)</button>
              )}
            </div>
            {mode === 'inhouse' ? (
              <div className="space-y-3">
                <Label className="text-xs">CLI Scheme</Label>
                <select value={schemeId} onChange={(e) => setSchemeId(e.target.value)}
                  className="w-full h-9 border border-gray-200 px-3 text-sm" data-testid="issue-scheme-select">
                  {eligible?.schemes?.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.is_primary ? '★ ' : ''}{s.name} — {s.insurer_name} (R{Number(s.premium_rate_per_1000).toFixed(2)}/R1000)
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400">★ = primary (auto-issued on disbursement when product is set to mandatory).</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2"><Label>Insurer Name *</Label><Input value={byo.external_insurer_name} onChange={(e) => setByo({ ...byo, external_insurer_name: e.target.value })} placeholder="e.g., Old Mutual" data-testid="byo-insurer-name" /></div>
                <div><Label>External Policy # *</Label><Input value={byo.external_policy_number} onChange={(e) => setByo({ ...byo, external_policy_number: e.target.value })} data-testid="byo-policy-number" /></div>
                <div><Label>Insurer FSP # *</Label><Input value={byo.external_fsp_number} onChange={(e) => setByo({ ...byo, external_fsp_number: e.target.value })} data-testid="byo-fsp-number" /></div>
                <div><Label>Cover Amount (R) *</Label><Input type="number" value={byo.cover_amount} onChange={(e) => setByo({ ...byo, cover_amount: e.target.value })} data-testid="byo-cover-amount" /></div>
                <div></div>
                <div><Label>Policy Start</Label><Input type="date" value={byo.policy_start_date} onChange={(e) => setByo({ ...byo, policy_start_date: e.target.value })} /></div>
                <div><Label>Policy End</Label><Input type="date" value={byo.policy_end_date} onChange={(e) => setByo({ ...byo, policy_end_date: e.target.value })} /></div>
                <div className="col-span-2 p-2 bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                  <b>NCA right:</b> the borrower may use their own insurance provided cover ≥ outstanding + 2 months premium and includes death + permanent disability. Verify the policy schedule and capture cession-of-rights in favour of the bank.
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowIssue(false)}>Cancel</Button>
              <Button className="bg-[#1f1f1f] text-white" disabled={issuing} onClick={issuePolicy} data-testid="confirm-issue-btn">
                {issuing ? 'Issuing...' : (mode === 'byo' ? 'Capture BYO Policy' : 'Issue Policy')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LoansPage;
